import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articlesData } from '@/mocks/articleMocks';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articlesData.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h2>
          <p className="text-foreground-500 mb-4">您要查看的文章不存在或已被移除</p>
          <Link to="/" className="text-sm px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.cover,
    publisher: {
      '@type': 'Organization',
      name: 'aigei',
    },
  };

  const relatedArticles = articlesData
    .filter((a) => article.relatedArticles.includes(a.id))
    .map(({ content, ...rest }) => rest);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`aigei,${article.tags.join(',')},游戏开发,素材教程`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        schema={articleSchema}
      />

      <main>
        <div className="pt-20 pb-0 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '文章资讯', href: '/articles' },
              { label: article.title },
            ]} />
          </div>
        </div>

        <article className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {article.tags.map((tag, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="text-foreground-700">{article.author}</span>
                  </div>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>

              <div className="aspect-[2/1] rounded-xl overflow-hidden mb-10">
                <img
                  src={article.cover}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="prose-custom">
                {article.content.map((block, index) => {
                  if (block.type === 'heading') {
                    return (
                      <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p key={index} className="text-foreground-700 leading-relaxed mb-5 text-base">
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-thumb-up-line text-lg"></i>
                      <span>有帮助 (128)</span>
                    </button>
                    <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-share-forward-line text-lg"></i>
                      <span>分享</span>
                    </button>
                    <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-bookmark-line text-lg"></i>
                      <span>收藏</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group flex gap-4 bg-background-50 rounded-xl p-4 hover:bg-background-50 transition-all duration-300 cursor-pointer"
                    >
                      <img
                        src={ra.cover}
                        alt={ra.title}
                        className="w-24 h-24 md:w-28 md:h-28 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500">{ra.author} · {ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}